// account_deletions_manager.jsx
// Triage queue for user-submitted account & data deletion requests. The public
// Navela webapp deletion form (navela.app/account-delete) feeds this list via the
// backend's navela-account-deletions package. Admins review each request, move it
// through pending → processing → completed (or rejected), and can remove rows.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  UserX, Search, Trash2, RefreshCw, ChevronLeft, ChevronRight, Eye, Mail, Check, X, Send, AlertTriangle,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Modal, Field, inputClass, Button, Loading, EmptyState, ConfirmDialog } from './navela_ui';

const PAGE_SIZE = 25;
const STATUSES = ['pending', 'processing', 'completed', 'rejected'];

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
  processing: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
  completed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  rejected: 'bg-rose-50 text-rose-700 ring-1 ring-rose-100',
};
const label = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '—');

function StatusBadge({ status }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-600'}`}>
      {label(status)}
    </span>
  );
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Small yes/no indicator for the notification-email flags.
function Sent({ ok }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 text-emerald-600" title="Sent"><Check className="h-3.5 w-3.5" /></span>
  ) : (
    <span className="inline-flex items-center gap-1 text-slate-300" title="Not sent"><X className="h-3.5 w-3.5" /></span>
  );
}

export default function AccountDeletionsManager() {
  const { fetchDeletionRequests, updateDeletionStatus, deleteDeletionRequest, resendDeletionEmails, deleteUserAccount, loading } = useNavela();

  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const [selected, setSelected] = useState(null); // request being triaged
  const [statusDraft, setStatusDraft] = useState('pending');
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resending, setResending] = useState(false);
  const [accountDeleteConfirm, setAccountDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await fetchDeletionRequests({
        search: search || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset: opts.offset ?? offset,
      });
      setRequests(data?.requests ?? []);
      setTotal(data?.total_count ?? 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load deletion requests');
    }
  }, [fetchDeletionRequests, search, status, offset]);

  // Debounced reload on filter change.
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load({ offset: 0 }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  useEffect(() => {
    load({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (r) => {
    setSelected(r);
    setStatusDraft(r.status || 'pending');
    setNoteDraft(r.admin_note || '');
  };
  const closeDetail = () => { setSelected(null); setSaving(false); };

  const saveStatus = async () => {
    if (!selected) return;
    // Completing a request performs the ACTUAL account deletion — route it through
    // the confirmed delete flow instead of a plain status update.
    if (statusDraft === 'completed') {
      setAccountDeleteConfirm(true);
      return;
    }
    setSaving(true);
    try {
      await updateDeletionStatus(selected.id, {
        status: statusDraft,
        admin_note: noteDraft.trim() === '' ? null : noteDraft.trim(),
      });
      toast.success('Request updated');
      closeDetail();
      load();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!selected) return;
    setResending(true);
    try {
      const res = await resendDeletionEmails(selected.id);
      if (res?.user_email_sent && res?.team_email_sent) {
        toast.success('Notification emails sent');
      } else {
        toast.error(res?.email_error ? `Email issue: ${res.email_error}` : 'Some emails failed to send');
      }
      // Reflect the new flags/error in the open modal and the list.
      setSelected((s) => (s ? {
        ...s,
        user_email_sent: res?.user_email_sent ?? s.user_email_sent,
        team_email_sent: res?.team_email_sent ?? s.team_email_sent,
        email_error: res?.email_error ?? null,
      } : s));
      load();
    } catch (err) {
      toast.error(err.message || 'Resend failed');
    } finally {
      setResending(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selected) return;
    setDeletingAccount(true);
    try {
      const res = await deleteUserAccount(selected.id);
      if (res?.user_found === false) {
        toast(res?.message || 'No account found for that email — nothing deleted.', { icon: 'ℹ️' });
      } else {
        toast.success(res?.message || 'Account deleted');
      }
      setAccountDeleteConfirm(false);
      closeDetail();
      load();
    } catch (err) {
      toast.error(err.message || 'Account deletion failed');
    } finally {
      setDeletingAccount(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDeletionRequest(deleteTarget.id);
      toast.success('Request deleted');
      setDeleteTarget(null);
      const nextOffset = requests.length === 1 && offset >= PAGE_SIZE ? offset - PAGE_SIZE : offset;
      setOffset(nextOffset);
      load({ offset: nextOffset });
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const go = (nextOffset) => { setOffset(nextOffset); load({ offset: nextOffset }); };

  const isEmpty = !loading && requests.length === 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <UserX className="h-5 w-5 text-pr-navy-600" /> Deletion Requests
          </h2>
          <p className="text-sm text-slate-500">{total.toLocaleString()} account deletion request{total === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => load()} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email…" className={`${inputClass} pl-9`} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputClass} w-44`}>
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {loading && requests.length === 0 ? (
        <Loading label="Loading deletion requests…" />
      ) : isEmpty ? (
        <EmptyState icon={UserX} title="No deletion requests" hint="When a user submits the webapp deletion form, it appears here." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Emails</th>
                  <th className="px-4 py-3">Requested</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.email}</div>
                      <div className="text-xs text-slate-400">#{r.id}{r.source ? ` · ${r.source}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs text-slate-600">
                      <span className="line-clamp-2">{r.reason || <span className="text-slate-300">—</span>}</span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1" title="User acknowledgement email">U <Sent ok={r.user_email_sent} /></span>
                        <span className="inline-flex items-center gap-1" title="Team notification email">T <Sent ok={r.team_email_sent} /></span>
                        {r.email_error && (
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title={r.email_error} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openDetail(r)} className="rounded-lg p-2 text-slate-400 hover:bg-pr-navy-50 hover:text-pr-navy-600" title="View / triage"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteTarget(r)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span>Page {page} of {pageCount}</span>
            <div className="flex items-center gap-1">
              <button disabled={offset === 0} onClick={() => go(Math.max(0, offset - PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= pageCount} onClick={() => go(offset + PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={selected !== null}
        title="Deletion request"
        subtitle={selected ? `#${selected.id} · ${selected.email}` : ''}
        onClose={closeDetail}
        footer={
          <>
            <Button variant="secondary" onClick={handleResend} disabled={resending || saving} title="Re-send the user + team notification emails">
              <Send className="h-4 w-4" /> {resending ? 'Resending…' : 'Resend emails'}
            </Button>
            <Button variant="secondary" onClick={closeDetail} disabled={saving || resending}>Close</Button>
            <Button onClick={saveStatus} disabled={saving || resending}>{saving ? 'Saving…' : 'Save changes'}</Button>
          </>
        }
      >
        {selected && (
          <div className="space-y-5">
            {selected.email_error ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold">Notification emails not fully sent</p>
                  <p className="mt-0.5 break-words text-amber-700">{selected.email_error}</p>
                  <p className="mt-1 text-xs text-amber-600">Fix the cause, then use “Resend emails” below.</p>
                </div>
              </div>
            ) : (
              (selected.user_email_sent && selected.team_email_sent) && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
                  <Check className="h-4 w-4 shrink-0" /> Both notification emails were sent.
                </div>
              )
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Detail label="Email" value={<a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-pr-navy-600 hover:underline"><Mail className="h-3.5 w-3.5" /> {selected.email}</a>} />
              <Detail label="Source" value={selected.source || '—'} />
              <Detail label="Locale" value={selected.locale || '—'} />
              <Detail label="Requested" value={fmtDate(selected.created_at)} />
              <Detail label="Last updated" value={fmtDate(selected.updated_at)} />
              <Detail label="Processed" value={fmtDate(selected.processed_at)} />
              <Detail label="User email sent" value={selected.user_email_sent ? 'Yes' : 'No'} />
              <Detail label="Team email sent" value={selected.team_email_sent ? 'Yes' : 'No'} />
              {selected.ip_address && <Detail label="IP address" value={selected.ip_address} />}
            </div>

            <Field label="Reason (from user)">
              <div className="min-h-[42px] whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {selected.reason || <span className="text-slate-400">No reason provided</span>}
              </div>
            </Field>

            <Field label="Status" hint="Setting “Completed” deletes the user's account & data (you'll be asked to confirm).">
              <select className={inputClass} value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s === 'completed' ? 'Completed (deletes account)' : label(s)}</option>)}
              </select>
            </Field>

            <Field label="Admin note" hint="Internal only — not shown to the user.">
              <textarea rows={3} className={inputClass} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Add a note about how this request was handled…" />
            </Field>

            {/* Danger zone — the actual, irreversible account deletion. */}
            <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
              <p className="flex items-center gap-1.5 text-sm font-bold text-rose-800">
                <AlertTriangle className="h-4 w-4" /> Danger zone
              </p>
              <p className="mt-1 text-xs leading-relaxed text-rose-600">
                Permanently delete this user’s Navela account (<span className="font-semibold">{selected.email}</span>) and all
                their data — profile, tracked ships and login records. This cannot be undone and is separate from removing the
                request from the list.
              </p>
              <Button variant="danger" className="mt-3" onClick={() => setAccountDeleteConfirm(true)} disabled={deletingAccount}>
                <UserX className="h-4 w-4" /> {deletingAccount ? 'Deleting…' : 'Delete account & data'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={accountDeleteConfirm}
        title="Delete this user's account?"
        message={selected ? `This permanently deletes the Navela account for “${selected.email}” and all associated data (profile, tracked ships, login records). This cannot be undone.` : ''}
        confirmLabel="Delete account"
        busy={deletingAccount}
        onConfirm={handleDeleteAccount}
        onCancel={() => setAccountDeleteConfirm(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this request?"
        message={deleteTarget ? `The deletion request from “${deleteTarget.email}” will be permanently removed from the queue. This does not undo any deletion already performed.` : ''}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// Small read-only labelled value used inside the detail modal.
function Detail({ label: l, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</p>
      <p className="mt-0.5 text-sm text-slate-700">{value}</p>
    </div>
  );
}
